import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import {
	invalidateGetPublicAuthKeys,
	useDeletePublicAuthKeyMutation,
	useGetPublicAuthKeys,
	usePostPublicAuthKeyMutation,
} from '../../../../queries';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../components/DataGrid';
import { Dropdown, DropdownContent, DropdownEllipsisTrigger, DropdownItem } from '../../../components-v2/Dropdown';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	IConfirmationDialogOption,
} from '../../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { TextInput } from '../../../components/TextInput';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

export const ManageAPIKeys = () => {
	const [showingAddRow, setShowingAddRow] = React.useState(false);
	const [description, setDescription] = React.useState('');
	const { logApiError } = useEventLogging('ManageAPIKeys');
	const errorMessages = useErrorMessages();
	const [keyToDelete, setKeyToDelete] = React.useState<Api.IAPIKeyReference | null>(null);

	const userSession = useUserSession();
	const history = useHistory();

	if (!userSession.account.features.publicApi?.enabled) {
		history.push('/dashboard');
	}

	const apiKeysQuery = useGetPublicAuthKeys({
		enabled: Boolean(userSession.account.features.publicApi?.enabled),
		refetchOnWindowFocus: false,
	});

	const createPublicAuthKey = usePostPublicAuthKeyMutation();

	const deletePublicAuthKey = useDeletePublicAuthKeyMutation({
		onSuccess: () => {
			invalidateGetPublicAuthKeys({});
			toast.success('API Key has been deleted');
		},
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
			logApiError('DeletePublicApiKey-Error', error);
		},
	});

	const onDeleteConfirmationRequestClose = (result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
		setKeyToDelete(null);
		if (result?.isDestructive && !cancel) {
			deletePublicAuthKey.mutate(keyToDelete.blobId);
		}
	};

	if (apiKeysQuery.isPending) {
		return <LoadingSpinner type='large' className={css(baseStyleSheet.absoluteCenter)} />;
	}

	const showAddRow = () => {
		setDescription('');
		setShowingAddRow(true);
	};

	const saveNewKey = () => {
		const keyPromise = createPublicAuthKey.mutateAsync({ description });

		// Initiate clipboard write synchronously within the user gesture so Safari allows it.
		// The ClipboardItem data resolves once the API responds.
		navigator.clipboard
			.write([
				new ClipboardItem({
					'text/plain': keyPromise.then(key => new Blob([key], { type: 'text/plain' })),
				}),
			])
			.then(() => {
				toast.success('New key has been copied to your clipboard');
			})
			.catch(() => {
				toast.error('Failed to copy new key to clipboard');
			});

		keyPromise
			.then(() => {
				setDescription('');
				setShowingAddRow(false);
				invalidateGetPublicAuthKeys({});
			})
			.catch((error: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
				logApiError('CreatePublicApiKey-Error', error);
			});
	};

	const copyKey = (keyId: string) => {
		const fetchPromise = userSession.webServiceHelper.callAsync<string>(
			Api.ImpersonationBroker.composeApiUrl({ urlPath: `public/auth/key/${keyId}` }),
			'GET'
		);

		// Initiate clipboard write synchronously within the user gesture so Safari allows it.
		// The ClipboardItem data resolves once the API responds.
		navigator.clipboard
			.write([
				new ClipboardItem({
					'text/plain': fetchPromise.then(key => new Blob([key], { type: 'text/plain' })),
				}),
			])
			.then(() => {
				toast.success('Key has been copied to your clipboard');
			})
			.catch(() => {
				toast.error('Failed to copy key to clipboard');
			});
	};

	const deleteKey = (key: Api.IAPIKeyReference) => {
		setKeyToDelete(key);
	};

	return (
		<div className={css(styleSheet.container)}>
			<header className={css(styleSheet.header)}>
				<section>
					<h1 className={css(baseStyleSheet.breadcrumbTitle)}>API Keys</h1>
					<small>Generate and manage API keys to integrate with third-party apps</small>
				</section>

				<section className={css(styleSheet.headerActions)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={showAddRow}>
						Generate New Key
					</button>
				</section>
			</header>
			<div className={css(styleSheet.card)}>
				<DataGrid>
					<DataGridColumns>
						<DataGridColumn>Description</DataGridColumn>
						<DataGridColumn>Key</DataGridColumn>
						<DataGridColumn>Added</DataGridColumn>
						<DataGridColumn>Last Used</DataGridColumn>
						<DataGridColumn>Actions</DataGridColumn>
					</DataGridColumns>
					<DataGridRows>
						{apiKeysQuery.data.map((apiKey: Api.IAPIKeyReference) => {
							const lastUsedDate = apiKey.lastUsedDate ? moment(apiKey.lastUsedDate).format('MM/DD/YYYY') : 'Never';

							return (
								<DataGridRow key={`auth-keys-${apiKey.blobId}`}>
									<DataGridRowItem>{apiKey.description}</DataGridRowItem>
									<DataGridRowItem>
										<span>**** **** **** ****</span>
										<button onClick={() => copyKey(apiKey.blobId)} className={css(styleSheet.copyKeyButton)}>
											Copy to Clipboard
										</button>
									</DataGridRowItem>
									<DataGridRowItem>{moment(apiKey.creationDate).format('MM/DD/YYYY')}</DataGridRowItem>
									<DataGridRowItem>{lastUsedDate}</DataGridRowItem>
									<DataGridRowItem>
										<Dropdown>
											<DropdownEllipsisTrigger />
											<DropdownContent>
												<DropdownItem onClick={() => deleteKey(apiKey)}>Delete</DropdownItem>
											</DropdownContent>
										</Dropdown>
									</DataGridRowItem>
								</DataGridRow>
							);
						})}
						{showingAddRow ? (
							<DataGridRow>
								<DataGridRowItem>
									<TextInput
										inputId='api-key-description'
										placeholder='Description'
										onChange={e => setDescription(e.target.value)}
										type='text'
										value={description}
									/>
								</DataGridRowItem>
								<DataGridRowItem />
								<DataGridRowItem />
								<DataGridRowItem />
								<DataGridRowItem>
									<button className={css(baseStyleSheet.ctaButton)} onClick={saveNewKey}>
										Generate
									</button>
								</DataGridRowItem>
							</DataGridRow>
						) : null}
						{apiKeysQuery.data?.length < 1 ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>No data found</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
			</div>

			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!keyToDelete,
					onRequestClose: onDeleteConfirmationRequestClose,
				}}
				options={DefaultDeleteConfirmationOptions}
				title={
					<span>
						{`Are you sure you want to delete the "${keyToDelete?.description || ''}" key?`}
						<br />
						<b>The key will no longer work.</b>
					</span>
				}
			/>
		</div>
	);
};
